import { compact, partial, uniq } from '@quromedical/utils';
import type { Device, DeviceDeviceName, DeviceUseStatement } from 'fhir/r4';

import {
  DeviceCode,
  DeviceUseStatementCode,
  IdentifierCode,
  system,
} from '../coding';
import type { WithId } from '../types';
import { createReference, getReferenceId, type Voidable } from './id';
import type { CreateReference } from './reference';

export const mapRelayToDevice = (name: string): Device => ({
  resourceType: 'Device',
  type: {
    coding: [
      {
        system,
        code: DeviceCode.relay,
      },
    ],
  },
  status: 'active',
  identifier: [
    {
      system,
      type: {
        coding: [
          {
            code: IdentifierCode.deviceType,
          },
        ],
      },
      value: DeviceCode.relay,
    },
  ],
  deviceName: [
    {
      name,
      type: 'user-friendly-name',
    },
  ],
});

export const mapPatchToDevice = (name: string): Device => ({
  resourceType: 'Device',
  type: {
    coding: [
      {
        system,
        code: DeviceCode.patch,
      },
    ],
  },
  status: 'active',
  identifier: [
    {
      system,
      type: {
        coding: [
          {
            code: IdentifierCode.deviceType,
          },
        ],
      },
      value: DeviceCode.patch,
    },
  ],
  deviceName: [
    {
      name,
      type: 'manufacturer-name',
    },
  ],
});

export const isDeviceOfType = (code: DeviceCode, device: Device): boolean => {
  const codes = device.type?.coding;
  const types = codes?.map((c) => c.code);
  const hasCode = !!types?.includes(code);

  const identifiers = device.identifier;
  const values = identifiers?.map((i) => i.value);
  const hasValue = !!values?.includes(code);

  return hasCode || hasValue;
};

/**
 * Gets all type codes for a device
 */
export const getDeviceTypeCodes = (device: Device): DeviceCode[] => {
  const codes = device.type?.coding;

  // we store the types in the identifier as well so that we can search on this in FHIR
  const identifiers =
    device.identifier?.map((i) => i.value as DeviceCode | undefined) || [];

  // we prefer codes if we can find them
  const types = codes?.map((c) => c.code as DeviceCode | undefined) || [];

  return uniq(compact([...types, ...identifiers]));
};

export const isDevicePatch = partial(isDeviceOfType, DeviceCode.patch);

export const isDeviceRelay = partial(isDeviceOfType, DeviceCode.relay);

export const isDeviceBand = partial(isDeviceOfType, DeviceCode.band);

interface EffectiveTimes {
  startTime: Date;
  endTime?: Date;
}

export const getEffectiveTimes = (
  deviceUseStatement: DeviceUseStatement,
): EffectiveTimes => {
  const startTime = deviceUseStatement.timingPeriod?.start
    ? new Date(deviceUseStatement.timingPeriod.start)
    : new Date();

  const endTime = deviceUseStatement.timingPeriod?.end
    ? new Date(deviceUseStatement.timingPeriod.end)
    : undefined;

  return { startTime, endTime };
};

/**
 * Gets the device name of the type provided if it exists
 * @param device
 * @param types will use this to find the relevant name, returns the first name if no matches are
 * found
 * @returns
 */
export const getDeviceName = (
  device?: Device,
  ...types: DeviceDeviceName['type'][]
): string | undefined => {
  if (!device?.deviceName?.length) {
    return undefined;
  }

  const foundName = device.deviceName.find((n) => types.includes(n.type));

  if (!foundName) {
    return device.deviceName[0].name;
  }

  return foundName.name;
};

export const getDeviceFriendlyName = (device?: Device): string | undefined =>
  getDeviceName(device, 'user-friendly-name');

export const getDeviceFriendlyNames = (devices: Device[]): string[] => {
  const names = devices.map(getDeviceFriendlyName);

  return compact(names);
};

export const createDeviceRef = (device: WithId<Device>): CreateReference => {
  const display = getDeviceFriendlyName(device);

  const ref = createReference('Device', device.id, display);

  return ref;
};

/**
 * Filter out `DeviceUseStatements` which contain a specific device reference
 * @param deviceId
 * @param deviceUseStatements
 * @returns
 */
export const getDeviceUseStatementsForDevice = (
  deviceId: string,
  deviceUseStatements: DeviceUseStatement[],
): DeviceUseStatement[] => {
  const result = deviceUseStatements.filter((o) => {
    const ref = getReferenceId(o.device);

    return ref === deviceId;
  });

  return result;
};

export const getDevicesByCode = (devices: Device[], code: DeviceCode) =>
  devices.filter((device) => isDeviceOfType(code, device));

export type DeviceStatus = Device['status'];

export type LifecycleType = 'disposable' | 'reusable';
export interface DeviceLifecycle {
  lifecycle: LifecycleType;
  deviceCode: DeviceCode;
}
export const DeviceLifecycleMap: Record<
  DeviceUseStatementCode,
  DeviceLifecycle
> = {
  [DeviceUseStatementCode.patch]: {
    deviceCode: DeviceCode.patch,
    lifecycle: 'disposable',
  },
  [DeviceUseStatementCode.band]: {
    deviceCode: DeviceCode.band,
    lifecycle: 'reusable',
  },
  [DeviceUseStatementCode.relay]: {
    deviceCode: DeviceCode.relay,
    lifecycle: 'reusable',
  },
  [DeviceUseStatementCode.glucose]: {
    deviceCode: DeviceCode.glucose,
    lifecycle: 'reusable',
  },
  [DeviceUseStatementCode.bloodPressure]: {
    deviceCode: DeviceCode.bloodPressure,
    lifecycle: 'reusable',
  },
};

const deviceDetails: DeviceLifecycle[] = Object.values(DeviceLifecycleMap);

export const getDeviceLifecycle = (
  code?: DeviceUseStatementCode,
): Voidable<DeviceLifecycle> => {
  if (!code) {
    return undefined;
  }
  return DeviceLifecycleMap[code];
};

export const getLifecycleForDeviceCode = (
  deviceCode?: DeviceCode,
): Voidable<DeviceLifecycle> => {
  if (!deviceCode) {
    return undefined;
  }
  return deviceDetails.find((device) => device.deviceCode === deviceCode);
};
